import {
  AsYouType,
  type CountryCode,
  type E164Number,
  parsePhoneNumberWithError,
} from 'libphonenumber-js/min'
import { useCallback } from 'react'

function formatPhoneValue(
  phone: string | null | undefined,
  countryCode: CountryCode = 'US'
): string {
  if (!phone) return ''

  const normalizedPhone = String(phone).trim()
  try {
    const parsed = parsePhoneNumberWithError(normalizedPhone, countryCode)

    if (!parsed.isValid()) {
      if (!normalizedPhone.startsWith('+')) {
        return new AsYouType(countryCode).input(normalizedPhone) ?? normalizedPhone
      }

      if (parsed.countryCallingCode === '1') {
        return parsed.formatNational()
      }

      return new AsYouType(countryCode).input(normalizedPhone) ?? normalizedPhone
    }

    if (parsed.countryCallingCode === '1') {
      return parsed.formatNational()
    }

    return parsed.formatInternational()
  } catch {
    return new AsYouType(countryCode).input(normalizedPhone) ?? normalizedPhone
  }
}

export const formatPhone = formatPhoneValue

export type PhoneFormatter = {
  formatPhone: (phone: string | null | undefined, countryCode?: CountryCode) => string
  formatToE164: (phone: string, countryCode?: CountryCode) => E164Number | null
  isValidPhone: (phone: string, countryCode?: CountryCode) => boolean
}

export const usePhoneFormatter = (): PhoneFormatter => {
  // Real-time formatting as user types
  const formatPhone = useCallback(
    (phone: string | null | undefined, countryCode: CountryCode = 'US') => {
      return formatPhoneValue(phone, countryCode)
    },
    []
  )

  const formatToE164 = useCallback((phone: string, countryCode: CountryCode = 'US') => {
    try {
      const parsed = parsePhoneNumberWithError(phone, countryCode)
      return parsed ? parsed.number : null
    } catch {
      return null
    }
  }, [])

  // Validate phone number
  const isValidPhone = useCallback((phone: string, countryCode: CountryCode = 'US') => {
    try {
      const parsed = parsePhoneNumberWithError(phone, countryCode)
      return parsed ? parsed.isValid() : false
    } catch {
      return false
    }
  }, [])

  return {
    formatPhone,
    formatToE164,
    isValidPhone,
  }
}
